var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { LogFactory } from '../config/logging';
import ConfigsManager from '../config/configs';
import { sleep } from '../../JiveSessionUtils';
import { castMediaError } from './RTCMediaErrors';
import { setOpusAttributes, setApplicationSpecificBitrate } from './RTCBandwidthHandler';
import { releaseStream } from '../../devices';
import { Browser, getBrowserName, getMixedStreams } from '../../helpers';
// @ts-ignore
import { interval } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
var Logger = LogFactory('RTCHandler');
export var PEER_CONNECTION_CLOSED_ERROR = 'PEER_CONNECTION_CLOSED_ERROR';
var MIN_ELAPSED_TIME_BETWEEN_DISCONNECTIONS = 3000;
/**
 * Class representing a Real Time Communication Handling methods.
 * The constructor is called internally within JiveRTC when a call is created.
 */
var RTCConnectionHandler = /** @class */ (function () {
    /**
     * Create a RTCConnectionHandler.
     * @param {Object} mediaConstraints - The mediaConstraints to be used for getUserMedia.
     * @param {Object} options - The options to be used for createOffer.
     */
    function RTCConnectionHandler() {
        var _this = this;
        this._stream = null;
        this._mixedStream = null;
        this._remoteSDP = null;
        this.exchangeQueue = [];
        this.offersInFlight = 0;
        this.previousDisconnectionTimestamp = 0;
        this.onNegotiationNeeded = function () { };
        this.onTrack = function (track) { };
        var iceServers = { 'iceServers': ConfigsManager.iceServers };
        Logger.log('Creating PeerConnection', iceServers);
        this._pc = new RTCPeerConnection(iceServers);
        this._pc.ontrack = function (track) { return _this._onTrackEvent(track); };
        this._pc.onconnectionstatechange = function () {
            /**
             * When the RTCPeerConnection fall into a disconnected state, we renegotiate to try to recover.
             * This can prevent having to wait for a no-media event from the server, which result in a perceptible audio
             * drop for the callers.
             */
            if (_this._pc.connectionState === 'disconnected' && _this._pc.iceConnectionState === 'disconnected') {
                Logger.log('RTCPeerConnection connection state: disconnected');
                setTimeout(function () {
                    var elapsedTimeSincePreviousDisconnection = Date.now() - _this.previousDisconnectionTimestamp;
                    Logger.log('Elapsed time since previous disconnection', elapsedTimeSincePreviousDisconnection);
                    _this.previousDisconnectionTimestamp = Date.now();
                    // The min elapsed time is to prevent falling into a tight renegotiation loop.
                    if (elapsedTimeSincePreviousDisconnection < MIN_ELAPSED_TIME_BETWEEN_DISCONNECTIONS || _this.offersInFlight > 0) {
                        Logger.log("Ignoring disconnection event since a re-offer might already be in progress. Will not attempt to reconnect.");
                    }
                    else {
                        Logger.log('Will send re-offer...');
                        // Make sure the call has not been closed
                        if (_this._pc && _this._pc.signalingState !== 'closed') {
                            _this.onNegotiationNeeded();
                        }
                    }
                }, 0);
            }
        };
        this._pc.onicecandidateerror = function (error) {
            Logger.warn('Ice candidate error', error);
        };
    }
    RTCConnectionHandler.prototype.getMedia = function (constraints, audioContext) {
        return __awaiter(this, void 0, void 0, function () {
            var _a, error_1;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 2, , 3]);
                        _a = this;
                        return [4 /*yield*/, this._getMedia(this.stream, constraints, audioContext)];
                    case 1:
                        _a.stream = _b.sent();
                        return [2 /*return*/, this.stream];
                    case 2:
                        error_1 = _b.sent();
                        Logger.error('Media error', error_1);
                        throw error_1;
                    case 3: return [2 /*return*/];
                }
            });
        });
    };
    RTCConnectionHandler.prototype._onTrackEvent = function (track) {
        Logger.log('Track Event', track);
        this.onTrack(track);
    };
    RTCConnectionHandler.prototype._getMedia = function (existingStream, constraints, audioContext) {
        return __awaiter(this, void 0, void 0, function () {
            var error, stream_1, mixedStream_1, error_2;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        Logger.log('GetUserMedia constraints', constraints);
                        if (!this._pc) {
                            error = {
                                errorCode: 'MEDIA_ERROR',
                                message: 'PeerConnection is not initialized'
                            };
                            throw error;
                        }
                        if (!existingStream) return [3 /*break*/, 2];
                        if (!(getBrowserName() === Browser.FIREFOX)) return [3 /*break*/, 2];
                        releaseStream(existingStream);
                        // JCS-3715. Calling getUserMedia too quickly after stopping the tracks
                        // sometimes throw an 'AbortError'. Can't use any flag or event to make sure getUserMedia()
                        // is safe to call so we introduce a delay with an arbitrary length as a workaround.
                        return [4 /*yield*/, new Promise(function (resolve) { return setTimeout(resolve, 50); })];
                    case 1:
                        // JCS-3715. Calling getUserMedia too quickly after stopping the tracks
                        // sometimes throw an 'AbortError'. Can't use any flag or event to make sure getUserMedia()
                        // is safe to call so we introduce a delay with an arbitrary length as a workaround.
                        _a.sent();
                        _a.label = 2;
                    case 2:
                        _a.trys.push([2, 6, , 7]);
                        return [4 /*yield*/, navigator.mediaDevices.getUserMedia(constraints)];
                    case 3:
                        stream_1 = _a.sent();
                        Logger.log('getUserMedia success', stream_1.getTracks());
                        mixedStream_1 = stream_1;
                        if (!(this.mixedStream && audioContext)) return [3 /*break*/, 5];
                        return [4 /*yield*/, getMixedStreams([this.mixedStream, stream_1], audioContext)];
                    case 4:
                        mixedStream_1 = _a.sent();
                        _a.label = 5;
                    case 5:
                        // Make sure the peer connection is still open. If the call is closed while
                        // we are in getUserMedia then the peer connection state will be closed here.
                        if (this._pc.connectionState === 'closed') {
                            throw ({
                                errorCode: PEER_CONNECTION_CLOSED_ERROR,
                                message: 'RTCConnectionHandler: _getMedia failed because peer connection is closed'
                            });
                        }
                        if (existingStream) {
                            releaseStream(existingStream);
                            this._pc.getSenders().map(function (sender) {
                                var track = mixedStream_1.getTracks().find(function (t) { return t.kind === (sender.track && sender.track.kind); });
                                if (track) {
                                    sender.replaceTrack(track);
                                }
                            });
                        }
                        else {
                            stream_1.getTracks().forEach(function (track) { return _this._pc.addTrack(track, stream_1); });
                        }
                        return [2 /*return*/, stream_1];
                    case 6:
                        error_2 = _a.sent();
                        if (error_2.errorCode === PEER_CONNECTION_CLOSED_ERROR) {
                            // Don't log this as an error, as it is expected behaviour if a call is ended
                            // before we have finished initializing the call.
                            throw error_2;
                        }
                        else {
                            Logger.error('Media error', error_2);
                            throw castMediaError(error_2);
                        }
                        return [3 /*break*/, 7];
                    case 7: return [2 /*return*/];
                }
            });
        });
    };
    RTCConnectionHandler.prototype.mixStreamIntoOutgoingAudio = function (streamToMix, audioContext) {
        return __awaiter(this, void 0, void 0, function () {
            var mixedStreamsAudioTracks_1, error_3;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (this.mixedStream) {
                            releaseStream(this.mixedStream);
                        }
                        this.mixedStream = streamToMix;
                        if (!this.stream) return [3 /*break*/, 4];
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, getMixedStreams([streamToMix, this.stream], audioContext)];
                    case 2:
                        mixedStreamsAudioTracks_1 = (_a.sent()).getAudioTracks();
                        this._pc.getSenders().forEach(function (sender) {
                            var mixedTrack = mixedStreamsAudioTracks_1
                                .find(function (t) { return t.kind === (sender.track && sender.track.kind); });
                            if (mixedTrack) {
                                sender.replaceTrack(mixedTrack);
                            }
                        });
                        return [3 /*break*/, 4];
                    case 3:
                        error_3 = _a.sent();
                        Logger.error('Media error', error_3);
                        throw error_3;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Creates a local sdp offer to negociate a media connection.
     * Note that this call must be matched to a corresponding call to "setRemoteAnswer".
     * In case of error, make sure to allow processing to continue by calling "clearCurrentOffer" instead.
     * @param options The RTC options to use when creating an offer.
     * @returns A promise which resolve to the sdp for the local offer.
     */
    RTCConnectionHandler.prototype.createOffer = function (options) {
        Logger.log('Creating new offer with options', options);
        return this._createOffer(options);
    };
    /**
     * Discards the current local offer and allows for processing new ones.
     * Clients should use this method when the client calls "createOffer" but fails to call "setRemoteAnswer"
     */
    RTCConnectionHandler.prototype.clearCurrentOffer = function () {
        if (this.offersInFlight > 0) {
            this.offersInFlight--;
        }
        this.handleNextOfferCreation();
    };
    RTCConnectionHandler.prototype._createOffer = function (options) {
        return __awaiter(this, void 0, void 0, function () {
            var offer, error_4, offerError;
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        // Because the signaling process of a peer connection is a state machine,
                        // we know that if the peer connection signaling state is not 'stable' it
                        // means that there is an ongoing exchange of offer and answer ongoing. In
                        // this case, to be able to create an offer, we need to wait until the
                        // exchange is over. The solution proposed is to differ the createOffer by
                        // putting it into a queue, and resolve it once the exchange is done.
                        // Note that we cannot actually rely on the peer connection signaling state here as it transitions asynchronously,
                        // which can cause race conditions. Instead, we maintain our own counter which keeps track of pending offers.
                        if (this.offersInFlight > 0) {
                            Logger.log('Queuing local description');
                            return [2 /*return*/, new Promise(function (resolve, reject) { return (_this.exchangeQueue.push({ resolve: resolve, reject: reject, options: options })); })];
                        }
                        this.offersInFlight++;
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._pc.createOffer(options)];
                    case 2:
                        offer = _a.sent();
                        Logger.log('New offer created successfully');
                        if (offer.sdp) {
                            offer.sdp = setOpusAttributes(offer.sdp, {
                                'stereo': '0',
                                'spropstereo': '0',
                                'usedtx': '1'
                            });
                        }
                        return [2 /*return*/, this._setLocalDescription(offer, options)];
                    case 3:
                        error_4 = _a.sent();
                        Logger.error('Creating offer failed', error_4);
                        offerError = {
                            errorCode: 'LOCAL_OFFER_ERROR',
                            message: error_4.message || 'Creating offer failed'
                        };
                        this.offersInFlight--;
                        throw offerError;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    RTCConnectionHandler.prototype._setLocalDescription = function (offer, options) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        Logger.log('Setting local description');
                        return [4 /*yield*/, this._pc.setLocalDescription(offer)];
                    case 1:
                        _a.sent();
                        Logger.log('Setting Local Description success', this.sdp);
                        if (!!options.iceRestart) return [3 /*break*/, 3];
                        return [4 /*yield*/, sleep(800)];
                    case 2:
                        _a.sent();
                        return [2 /*return*/, this.sdp];
                    case 3: return [4 /*yield*/, sleep(800)]; // Wait needed to make sure port and ufrag attributes are set in the sdp
                    case 4:
                        _a.sent(); // Wait needed to make sure port and ufrag attributes are set in the sdp
                        return [2 /*return*/, this.sdp];
                }
            });
        });
    };
    RTCConnectionHandler.prototype.setRemoteAnswer = function (sdp, bitrate) {
        return __awaiter(this, void 0, void 0, function () {
            var error, answerError, answerError;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this._pc) {
                            error = {
                                errorCode: 'REMOTE_ANSWER_ERROR',
                                message: 'PeerConnection is not initialized'
                            };
                            throw error;
                        }
                        if (bitrate) {
                            sdp = setApplicationSpecificBitrate(sdp, { video: bitrate });
                        }
                        Logger.log('Setting remote description answer', sdp);
                        // Similarly to the create offer stage, we know that if we receive an answer while
                        // the signaling state is not "have-local-offer", we know that there is something wrong
                        // in the exchange. We should only receive answers after creating an offer but
                        // before an answer has been received and passed into setLocalDescription.
                        // Note again that we have our own counter to check first to avoid race conditions.
                        if (this.offersInFlight === 0) {
                            answerError = {
                                errorCode: 'REMOTE_ANSWER_ERROR',
                                message: "Setting remote answer failed: Expected \"createOffer\" to have been called first"
                            };
                            throw answerError;
                        }
                        this.offersInFlight--;
                        if (this.offersInFlight > 0) {
                            Logger.warn('Remote Answer - Too many offers in flight', this.offersInFlight);
                        }
                        if (this._pc.signalingState !== 'have-local-offer') {
                            answerError = {
                                errorCode: 'REMOTE_ANSWER_ERROR',
                                message: "Setting remote answer failed: \"have-local-offer\" signaling state expected but got: \"" + this._pc.signalingState + "\""
                            };
                            throw answerError;
                        }
                        return [4 /*yield*/, this._setRemoteAnswer(sdp)
                            // Asynchronously handle offer creation(s) that were put earlier in the queue.
                        ];
                    case 1:
                        _a.sent();
                        // Asynchronously handle offer creation(s) that were put earlier in the queue.
                        this.handleNextOfferCreation();
                        return [2 /*return*/];
                }
            });
        });
    };
    RTCConnectionHandler.prototype.setBitRate = function (bitrate) {
        if (this._pc && this._pc.remoteDescription) {
            if (this._pc.remoteDescription.sdp) {
                this.setRemoteAnswer(this._pc.remoteDescription.sdp, bitrate);
            }
        }
    };
    RTCConnectionHandler.prototype.handleNextOfferCreation = function () {
        return __awaiter(this, void 0, void 0, function () {
            var exchange, localSdp, error_5;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(this.exchangeQueue.length !== 0)) return [3 /*break*/, 4];
                        exchange = this.exchangeQueue.shift();
                        if (!exchange) return [3 /*break*/, 4];
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._createOffer(exchange.options)];
                    case 2:
                        localSdp = _a.sent();
                        exchange.resolve(localSdp);
                        return [3 /*break*/, 4];
                    case 3:
                        error_5 = _a.sent();
                        exchange.reject(error_5);
                        return [3 /*break*/, 4];
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    RTCConnectionHandler.prototype._setRemoteAnswer = function (sdp) {
        return __awaiter(this, void 0, void 0, function () {
            var error_6, answerError;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.remoteSdp = sdp;
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, this._pc.setRemoteDescription({ sdp: this.remoteSdp, type: 'answer' })];
                    case 2:
                        _a.sent();
                        Logger.log('Setting remote description answer success');
                        return [3 /*break*/, 4];
                    case 3:
                        error_6 = _a.sent();
                        Logger.error('Setting remote description answer failure', error_6, this._pc.signalingState);
                        answerError = {
                            errorCode: 'REMOTE_ANSWER_ERROR',
                            message: error_6.message || 'Setting remote description answer failure'
                        };
                        throw answerError;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    RTCConnectionHandler.prototype.mute = function () {
        if (this.stream) {
            this.stream.getAudioTracks()[0].enabled = false;
        }
    };
    RTCConnectionHandler.prototype.unmute = function () {
        if (this.stream) {
            this.stream.getAudioTracks()[0].enabled = true;
        }
    };
    Object.defineProperty(RTCConnectionHandler.prototype, "sdp", {
        get: function () {
            if (this._pc && this._pc.localDescription) {
                return this._pc.localDescription.sdp;
            }
            return null;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RTCConnectionHandler.prototype, "remoteSdp", {
        get: function () {
            return this._remoteSDP;
        },
        set: function (sdp) {
            this._remoteSDP = sdp;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RTCConnectionHandler.prototype, "stream", {
        get: function () {
            return this._stream;
        },
        set: function (stream) {
            this._stream = stream;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(RTCConnectionHandler.prototype, "mixedStream", {
        get: function () {
            return this._mixedStream;
        },
        set: function (stream) {
            this._mixedStream = stream;
        },
        enumerable: true,
        configurable: true
    });
    RTCConnectionHandler.prototype.close = function () {
        if (this.mixedStream) {
            releaseStream(this.mixedStream);
            this.mixedStream = null;
        }
        if (this.stream) {
            releaseStream(this.stream);
            this.stream = null;
        }
        if (this._pc && this._pc.signalingState !== 'closed') {
            this._pc.close();
        }
    };
    RTCConnectionHandler.prototype.getStats = function () {
        return __awaiter(this, void 0, void 0, function () {
            var stats;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        stats = null;
                        if (!this._pc) return [3 /*break*/, 2];
                        return [4 /*yield*/, this._pc.getStats()];
                    case 1:
                        stats = _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/, stats];
                }
            });
        });
    };
    RTCConnectionHandler.prototype.observeRtcStats = function (statsInterval) {
        var _this = this;
        return interval(statsInterval).pipe(mergeMap(function () { return _this._pc.getStats(); }));
    };
    return RTCConnectionHandler;
}());
export { RTCConnectionHandler };
export default RTCConnectionHandler;
//# sourceMappingURL=RTCConnectionHandler.js.map